/**
 * PostHog session replay deep-link for Slack / admin alerts.
 *
 * IMPORTANT: Never use `{ingest}/report/?token=…` here. That path is PostHog's
 * **CSP violation** reporting endpoint. Opening it (or POSTing application/json)
 * returns:
 *   {"type":"invalid_payload","detail":"Failed to submit CSP report",…}
 *
 * Real session links go to the app UI:
 *   https://us.posthog.com/project/{projectId}/replay/{sessionId}
 */

const DEFAULT_PROJECT_ID = "445055";
const DEFAULT_APP_HOST = "https://us.posthog.com";

/** Map ingest hosts (us.i.posthog.com) → app UI hosts (us.posthog.com). */
export function toPostHogAppHost(host?: string | null): string {
  const raw = (host?.trim() || DEFAULT_APP_HOST).replace(/\/$/, "");
  try {
    const u = new URL(raw.includes("://") ? raw : `https://${raw}`);
    const h = u.hostname.toLowerCase();
    if (h === "us.i.posthog.com" || h === "i.posthog.com" || h === "app.posthog.com") {
      u.hostname = "us.posthog.com";
    } else if (h === "eu.i.posthog.com") {
      u.hostname = "eu.posthog.com";
    } else if (h.endsWith(".i.posthog.com")) {
      u.hostname = h.replace(/\.i\.posthog\.com$/, ".posthog.com");
    }
    return u.origin;
  } catch {
    return DEFAULT_APP_HOST;
  }
}

export function buildPostHogSessionReportUrl(opts: {
  sessionId?: string | null;
  /** Kept for callers; not required for the replay deep-link. */
  distinctId?: string | null;
  /** @deprecated CSP /report/?token=… — ignored. Use projectId. */
  token?: string | null;
  /** Ingest or app host; normalized to app UI origin. */
  host?: string | null;
  projectId?: string | null;
}): string | null {
  const sessionId = opts.sessionId?.trim();
  if (!sessionId) return null;

  const projectId = opts.projectId?.trim() || DEFAULT_PROJECT_ID;
  const appHost = toPostHogAppHost(opts.host);
  return `${appHost}/project/${encodeURIComponent(projectId)}/replay/${encodeURIComponent(sessionId)}`;
}
